import { useState } from "react";
import { MapPin, RefreshCcw } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { money } from "@/lib/format";
import { toast } from "sonner";
import SetbackVerification from "./SetbackVerification";
import { getApiErrorMessage } from "@/lib/apiError";

function toNumber(value) {
    const number = parseFloat(value || 0);
    return Number.isFinite(number) ? number : 0;
}

function fullDeliveryAddress(site) {
    return [site.delivery_address, site.delivery_city, site.delivery_state, site.delivery_zip]
        .filter(Boolean)
        .join(", ");
}

function routeLabel(routeType) {
    if (routeType === "factory_to_dealer") return "Factory to dealership";
    if (routeType === "factory_to_customer") return "Factory to customer";
    return "Dealership to customer";
}

export default function StepSite({ quote, update, customer }) {
    const site = quote.site || {};
    const [calculatingDelivery, setCalculatingDelivery] = useState(false);
    const setSite = (patch) => update({ site: { ...site, ...patch } });

    const useCustomerAddress = () => {
        if (!customer) return;
        setSite({
            delivery_address: customer.current_address,
            delivery_city: customer.current_city,
            delivery_state: customer.current_state,
            delivery_zip: customer.current_zip,
        });
    };

    const calculateDeliveryFromAddress = async () => {
        const destination = fullDeliveryAddress(site);
        const routeType = site.delivery_route_type || "dealer_to_customer";
        if (!destination) {
            toast.error("Enter the delivery address before calculating delivery miles");
            return;
        }

        setCalculatingDelivery(true);
        try {
            const [propertyLookupResult, deliveryResult] = await Promise.allSettled([
                api.post("/delivery-calculator/property-lookup", {
                    address: site.delivery_address,
                    city: site.delivery_city,
                    state: site.delivery_state || "FL",
                    zip: site.delivery_zip,
                    county: site.county || undefined,
                    zoning: site.zoning || undefined,
                }),
                api.post("/delivery-calculator/estimate", {
                    dealership_address: site.dealership_address || undefined,
                    factory_address: site.factory_address || undefined,
                    delivery_address: site.delivery_address,
                    delivery_city: site.delivery_city,
                    delivery_state: site.delivery_state,
                    delivery_zip: site.delivery_zip,
                    home_dimensions: quote.home?.dimensions,
                    home_type: quote.home?.home_type || quote.home?.type,
                    floors: quote.home?.floors,
                    sections: quote.home?.sections,
                    route_type: routeType,
                    factory_to_dealer_miles: site.factory_to_dealer_miles || undefined,
                    dealer_to_customer_miles: site.dealer_to_customer_miles || site.delivery_miles || undefined,
                    factory_to_customer_miles: site.factory_to_customer_miles || undefined,
                    home_origin: site.home_origin || "outside_florida",
                }),
            ]);

            if (deliveryResult.status === "rejected") {
                throw deliveryResult.reason;
            }

            const estimate = deliveryResult.value.data || {};
            const propertyLookup = propertyLookupResult.status === "fulfilled" ? propertyLookupResult.value.data || {} : {};
            const setbackLookup = propertyLookup.setbacks || {};
            const propertyWarning = propertyLookupResult.status === "rejected"
                ? getApiErrorMessage(propertyLookupResult.reason, "County lookup failed")
                : propertyLookup.warning || "";

            const miles = Math.ceil(Number(estimate.miles || 0));
            const routePatch = routeType === "factory_to_dealer"
                ? { factory_to_dealer_miles: miles, delivery_miles: miles }
                : routeType === "factory_to_customer"
                    ? { factory_to_customer_miles: miles, delivery_miles: miles }
                    : { dealer_to_customer_miles: miles, delivery_miles: miles };

            const patch = {
                delivery_route_type: estimate.route_type || routeType,
                ...routePatch,
                transport_sides: estimate.transport_sides || site.transport_sides,
                escort_count: estimate.escort_count || site.escort_count,
                delivery_calculation_source: estimate.maps_configured ? "google_distance_matrix" : "manual_fallback",
                delivery_calculation_note: estimate.formula_note,
                delivery_origin_address: estimate.origin_address,
                delivery_destination_address: estimate.destination_address,
                delivery_distance_text: estimate.distance_text,
                delivery_duration_text: estimate.duration_text,
                calculated_delivery_cost: estimate.delivery_cost,
                calculated_delivery_price: estimate.delivery_price,
                calculated_freight_cost: estimate.freight_cost,
                calculated_freight_price: estimate.freight_price,
                property_lookup_source: propertyLookup.source,
                property_lookup_confidence: propertyLookup.confidence,
                property_lookup_formatted_address: propertyLookup.formatted_address,
                property_lookup_warning: propertyWarning,
                municipality: propertyLookup.municipality || site.municipality,
                county: propertyLookup.county || site.county,
                delivery_city: propertyLookup.city || site.delivery_city,
                delivery_state: propertyLookup.state || site.delivery_state,
                delivery_zip: propertyLookup.zip || site.delivery_zip,
                latitude: propertyLookup.latitude || site.latitude,
                longitude: propertyLookup.longitude || site.longitude,
                setback_lookup: setbackLookup || site.setback_lookup,
                setback_source_name: site.setback_source_name || setbackLookup.source_name,
                setback_source_section: site.setback_source_section || setbackLookup.source_section,
                setback_source_url: site.setback_source_url || setbackLookup.source_url,
                setback_confidence_level: site.setback_confidence_level || setbackLookup.confidence_level || propertyLookup.confidence || "low",
                setback_verification_status: site.setback_verification_status || setbackLookup.verification_status || "needs_verification",
                setback_notes: site.setback_notes || setbackLookup.notes,
                delivery_warning: [estimate.warning, propertyWarning].filter(Boolean).join(" | "),
            };

            setSite(patch);

            toast.success(
                `${routeLabel(patch.delivery_route_type)} calculated: ${miles} miles, ${patch.transport_sides || "?"} side(s), ${patch.escort_count || "?"} escort(s)` +
                    (patch.county ? `, ${patch.county} County` : "")
            );
        } catch (err) {
            toast.error(getApiErrorMessage(err, "Delivery address calculation failed"));
        } finally {
            setCalculatingDelivery(false);
        }
    };

    return (
        <div className="space-y-4" data-testid="step-site">
            <Card className="space-y-5 rounded-xl border-slate-200 p-5">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Land & site</div>
                        <div className="text-xs text-slate-500">Where will the home be installed?</div>
                    </div>
                    {customer && (
                        <Button type="button" variant="outline" size="sm" onClick={useCustomerAddress} data-testid="step-site-use-customer-address">
                            Use customer address
                        </Button>
                    )}
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                        <div className="text-sm font-medium">Customer owns the land?</div>
                        <Switch checked={!!site.owns_land} onCheckedChange={(v) => setSite({ owns_land: v })} data-testid="step-site-owns-land-switch" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                        <div className="text-sm font-medium">Lien on land?</div>
                        <Switch checked={!!site.has_lien} onCheckedChange={(v) => setSite({ has_lien: v })} data-testid="step-site-has-lien-switch" />
                    </div>
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Land budget</Label>
                    <Input type="number" step="100" value={site.land_budget || ""} onChange={(e) => setSite({ land_budget: toNumber(e.target.value) })} />
                </div>
            </Card>

            <Card className="space-y-4 rounded-xl border-slate-200 p-5">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Delivery Route</div>
                    <div className="text-xs text-slate-500">Choose route setup first so the selected route is clear before calculation.</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                        <div>
                            <Label>Route type</Label>
                            <Select value={site.delivery_route_type || "dealer_to_customer"} onValueChange={(v) => setSite({ delivery_route_type: v })}>
                                <SelectTrigger data-testid="step-site-delivery-route-select"><SelectValue placeholder="Select route" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="dealer_to_customer">Dealership to customer</SelectItem>
                                    <SelectItem value="factory_to_dealer">Factory to dealership</SelectItem>
                                    <SelectItem value="factory_to_customer">Factory to customer</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Factory / origin address</Label>
                            <Input value={site.factory_address || ""} onChange={(e) => setSite({ factory_address: e.target.value })} placeholder="Factory address" />
                        </div>
                        <div>
                            <Label>Factory to dealer miles</Label>
                            <Input type="number" step="1" value={site.factory_to_dealer_miles || ""} onChange={(e) => setSite({ factory_to_dealer_miles: toNumber(e.target.value) })} placeholder="Auto/manual" />
                        </div>
                        <div>
                            <Label>Dealer to customer miles</Label>
                            <Input type="number" step="1" value={site.dealer_to_customer_miles || ""} onChange={(e) => setSite({ dealer_to_customer_miles: toNumber(e.target.value), delivery_miles: toNumber(e.target.value) })} placeholder="Auto/manual" data-testid="step-site-delivery-miles-input" />
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                        <div>
                            <Label>Factory to customer miles</Label>
                            <Input type="number" step="1" value={site.factory_to_customer_miles || ""} onChange={(e) => setSite({ factory_to_customer_miles: toNumber(e.target.value), delivery_miles: toNumber(e.target.value) })} placeholder="Auto/manual" />
                        </div>
                        <div>
                            <Label>Home origin</Label>
                            <Select value={site.home_origin || "outside_florida"} onValueChange={(v) => setSite({ home_origin: v })}>
                                <SelectTrigger><SelectValue placeholder="Select origin" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="florida">Made in Florida</SelectItem>
                                    <SelectItem value="outside_florida">Made outside Florida</SelectItem>
                                    <SelectItem value="dealer_lot">Already on dealer lot</SelectItem>
                                    <SelectItem value="tbd">TBD</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Transport sides / sections</Label>
                            <Input type="number" step="1" value={site.transport_sides || ""} onChange={(e) => setSite({ transport_sides: toNumber(e.target.value) })} placeholder="Auto from home" />
                        </div>
                        <div>
                            <Label>Escort count</Label>
                            <Input type="number" step="1" value={site.escort_count || ""} onChange={(e) => setSite({ escort_count: toNumber(e.target.value) })} placeholder="Auto from home" />
                        </div>
                    </div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div>
                            <Label>Manual selected-route fallback miles</Label>
                            <Input type="number" step="1" value={site.delivery_miles || ""} onChange={(e) => setSite({ delivery_miles: toNumber(e.target.value) })} placeholder="Fallback only" />
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-500">
                            Keep this as a fallback when route data is incomplete. The selected route still drives calculation behavior.
                        </div>
                    </div>
                </div>
            </Card>

            <Card className="space-y-4 rounded-xl border-slate-200 p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="text-sm font-semibold text-slate-900">Delivery Address</div>
                        <div className="text-xs text-slate-500">Where the home will be delivered and installed.</div>
                    </div>
                </div>
                <Input placeholder="Street" value={site.delivery_address || ""} onChange={(e) => setSite({ delivery_address: e.target.value })} data-testid="step-site-delivery-address-input" />
                <div className="grid grid-cols-3 gap-3">
                    <Input placeholder="City" value={site.delivery_city || ""} onChange={(e) => setSite({ delivery_city: e.target.value })} />
                    <Input placeholder="State" value={site.delivery_state || ""} onChange={(e) => setSite({ delivery_state: e.target.value })} />
                    <Input placeholder="ZIP" value={site.delivery_zip || ""} onChange={(e) => setSite({ delivery_zip: e.target.value })} />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Input placeholder="County" value={site.county || ""} onChange={(e) => setSite({ county: e.target.value })} />
                    <Input placeholder="Zoning" value={site.zoning || ""} onChange={(e) => setSite({ zoning: e.target.value })} />
                    <Input placeholder="Flood zone" value={site.flood_zone || ""} onChange={(e) => setSite({ flood_zone: e.target.value })} />
                </div>
                <div className="flex justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={calculateDeliveryFromAddress} disabled={calculatingDelivery} data-testid="step-site-calculate-delivery-button">
                        {calculatingDelivery ? <RefreshCcw className="mr-2 h-4 w-4 animate-spin" /> : <MapPin className="mr-2 h-4 w-4" />}
                        {calculatingDelivery ? "Calculating…" : "Calculate selected route"}
                    </Button>
                </div>
            </Card>

            <Card className="space-y-4 rounded-xl border-slate-200 p-5">
                <div className="text-sm font-semibold text-slate-900">Calculation Result</div>
                {(site.delivery_distance_text || site.delivery_duration_text || site.delivery_warning || site.property_lookup_source) && (
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                        <div className="font-medium text-slate-800">Delivery calculation</div>
                        {site.delivery_route_type && <div>Selected route: {routeLabel(site.delivery_route_type)}</div>}
                        {site.delivery_distance_text && <div>Route distance: {site.delivery_distance_text}</div>}
                        {site.delivery_duration_text && <div>Estimated drive time: {site.delivery_duration_text}</div>}
                        {site.delivery_origin_address && <div>From: {site.delivery_origin_address}</div>}
                        {site.delivery_destination_address && <div>To: {site.delivery_destination_address}</div>}
                        {site.county && <div>County: {site.county}</div>}
                        {site.property_lookup_source && <div>County source: {site.property_lookup_source} ({site.property_lookup_confidence || "unverified"})</div>}
                        {site.calculated_delivery_price ? <div>Calculated delivery: {money(site.calculated_delivery_price)}</div> : null}
                        {site.delivery_warning && <div className="text-amber-700">{site.delivery_warning}</div>}
                    </div>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                        <div>
                            <div className="text-sm font-medium">Wind Zone 3 required?</div>
                            <div className="text-xs text-slate-500">Compliance</div>
                        </div>
                        <Switch checked={!!site.wind_zone_required} onCheckedChange={(v) => setSite({ wind_zone_required: v })} data-testid="step-site-wz-required-switch" />
                    </div>
                    <div className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                        <div>
                            <div className="text-sm font-medium">Wind Zone 3 desired?</div>
                            <div className="text-xs text-slate-500">Customer preference</div>
                        </div>
                        <Switch checked={!!site.wind_zone_desired} onCheckedChange={(v) => setSite({ wind_zone_desired: v })} data-testid="step-site-wz-desired-switch" />
                    </div>
                </div>
            </Card>

            <Card className="space-y-4 rounded-xl border-slate-200 p-5">
                <SetbackVerification site={site} setSite={setSite} />
            </Card>

            <Card className="space-y-4 rounded-xl border-slate-200 p-5">
                <div>
                    <div className="text-sm font-semibold text-slate-900">Service Calculator Inputs</div>
                    <div className="text-xs text-slate-500">These fields drive automatic service, AC, delivery, freight, skirting, Dirt Pad, and bid-required line calculations.</div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4">
                    <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">AC calculation</div>
                    <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
                        <div>
                            <Label>AC type</Label>
                            <Select value={site.ac_type || "package"} onValueChange={(v) => setSite({ ac_type: v })}>
                                <SelectTrigger data-testid="step-site-ac-type-select"><SelectValue placeholder="Select AC type" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="package">Package unit</SelectItem>
                                    <SelectItem value="split">Split system</SelectItem>
                                    <SelectItem value="tbd">TBD</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>AC system</Label>
                            <Select value={site.ac_system_type || "straight_cool"} onValueChange={(v) => setSite({ ac_system_type: v })}>
                                <SelectTrigger><SelectValue placeholder="Select system" /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="straight_cool">Straight cool</SelectItem>
                                    <SelectItem value="heat_pump">Heat pump</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Manual AC tonnage override</Label>
                            <Input type="number" step="0.5" value={site.ac_tonnage_override || ""} onChange={(e) => setSite({ ac_tonnage_override: toNumber(e.target.value) })} placeholder="Optional" />
                        </div>
                    </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50/70 p-4" data-testid="step-site-dirt-pad-panel">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Dirt Pad</div>
                            <div className="mt-1 text-xs text-slate-500">Explicitly record whether a Dirt Pad is needed. Backend Pricing Engine v1.0 calculates retail/cost from the approved V05 load table.</div>
                        </div>
                        <Select value={site.dirt_pad_needed === true ? "yes" : site.dirt_pad_needed === false ? "no" : ""} onValueChange={(v) => setSite({ dirt_pad_needed: v === "yes", dirt_pad_loads: v === "yes" ? site.dirt_pad_loads : undefined })}>
                            <SelectTrigger className="w-[180px]" data-testid="step-site-dirt-pad-needed-select"><SelectValue placeholder="Dirt pad needed?" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="yes">Yes</SelectItem>
                                <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {site.dirt_pad_needed === true && (
                        <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-4">
                            <div>
                                <Label>Dirt loads</Label>
                                <Input type="number" step="1" min="1" value={site.dirt_pad_loads || ""} onChange={(e) => setSite({ dirt_pad_loads: toNumber(e.target.value) })} data-testid="step-site-dirt-pad-loads-input" placeholder="1–20" />
                            </div>
                            <div>
                                <Label>Manual cost override</Label>
                                <Input type="number" step="0.01" value={site.dirt_pad_cost_override || ""} onChange={(e) => setSite({ dirt_pad_cost_override: toNumber(e.target.value) })} placeholder="Optional" />
                            </div>
                            <div>
                                <Label>Manual retail override</Label>
                                <Input type="number" step="0.01" value={site.dirt_pad_price_override || ""} onChange={(e) => setSite({ dirt_pad_price_override: toNumber(e.target.value) })} placeholder="Optional" />
                            </div>
                            <div>
                                <Label>Override reason</Label>
                                <Input value={site.dirt_pad_override_reason || ""} onChange={(e) => setSite({ dirt_pad_override_reason: e.target.value })} placeholder="Required for overrides" />
                            </div>
                        </div>
                    )}
                    {site.dirt_pad_needed === false && (
                        <div className="mt-2 text-xs text-slate-500">Dirt Pad will not be added to the canonical required service lines.</div>
                    )}
                </div>


                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <div>
                        <Label>Water source</Label>
                        <Select value={site.water_source || "well"} onValueChange={(v) => setSite({ water_source: v })}>
                            <SelectTrigger data-testid="step-site-water-source-select"><SelectValue placeholder="Select water source" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="well">Well</SelectItem>
                                <SelectItem value="city_water">City water</SelectItem>
                                <SelectItem value="existing">Existing connection</SelectItem>
                                <SelectItem value="tbd">TBD</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Sewer source</Label>
                        <Select value={site.sewer_source || "septic"} onValueChange={(v) => setSite({ sewer_source: v })}>
                            <SelectTrigger data-testid="step-site-sewer-source-select"><SelectValue placeholder="Select sewer source" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="septic">Septic</SelectItem>
                                <SelectItem value="city_sewer">City sewer</SelectItem>
                                <SelectItem value="existing">Existing connection</SelectItem>
                                <SelectItem value="tbd">TBD</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {[
                        ["skirting_needed", "Skirting needed", true],
                        ["gutters_needed", "Gutters needed", false],
                        ["driveway_needed", "Driveway needed", false],
                        ["deck_needed", "Deck needed", false],
                        ["porch_needed", "Porch needed", false],
                        ["demolition_needed", "Demolition/removal needed", false],
                        ["land_clearing_needed", "Land clearing needed", false],
                        ["landscaping_needed", "Landscaping needed", false],
                    ].map(([key, label, defaultValue]) => (
                        <div key={key} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-2">
                            <div className="text-sm font-medium">{label}</div>
                            <Switch checked={site[key] === undefined ? defaultValue : !!site[key]} onCheckedChange={(v) => setSite({ [key]: v })} />
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}
